import { ApiClient } from '../apiClient';
import { ApiError } from '../errors';
import { config } from '../../config';

export interface AssistantStartRequest {
  turnstileToken?: string;
  cartId?: string;
  locale?: string;
  currency?: string;
  country?: string;
}

export interface AssistantStartResponse {
  conversationId: string;
  messagesLeft: number;
}

export interface AssistantMessageRequest {
  text: string;
  cartId?: string;
  currency?: string;
}

export type AssistantToolStatus = 'start' | 'end' | 'failed';

export interface AssistantToolEvent {
  name: string;
  status: AssistantToolStatus;
  result?: unknown;
}

export interface AssistantTranscriptMessage {
  role: 'user' | 'assistant';
  text: string;
  // Outputs of the tools the widget renders, in call order.
  tools?: { name: string; result: unknown }[];
}

export interface AssistantTranscript {
  conversationId: string;
  messagesLeft: number;
  messages: AssistantTranscriptMessage[];
}

export interface AssistantStreamHandlers {
  onText(delta: string): void;
  onTool(event: AssistantToolEvent): void;
  onDone(info: { messagesLeft: number }): void;
  onError(errorCode: string): void;
}

// The storefront assistant (docs/features/shop-assistant.md). Starting a
// conversation is a normal call; a reply streams as Server-Sent Events, which
// the axios client cannot read, so that one uses fetch.
export class AssistantService {
  constructor(private apiClient: ApiClient) {}

  async startConversation(body: AssistantStartRequest): Promise<AssistantStartResponse> {
    return this.apiClient.post<AssistantStartResponse>('/assistant/conversations', body);
  }

  // 404 unknown, 410 no longer continuable: the caller forgets the id.
  async getConversation(conversationId: string): Promise<AssistantTranscript> {
    return this.apiClient.get<AssistantTranscript>(
      `/assistant/conversations/${encodeURIComponent(conversationId)}`,
    );
  }

  async streamMessage(
    conversationId: string,
    body: AssistantMessageRequest,
    handlers: AssistantStreamHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    const locale = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]*)/)?.[1];
    const response = await fetch(
      `${config.GATEWAY_API_URL}/assistant/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-api-version': '2',
          ...(locale && { 'x-user-locale': locale }),
        },
        body: JSON.stringify(body),
        signal,
      },
    );

    if (!response.ok || !response.body) {
      let errorCode: string | undefined;
      try {
        const data = await response.json();
        if (data && typeof data.errorCode === 'string') errorCode = data.errorCode;
      } catch {
        // no JSON body
      }
      throw new ApiError(response.status, errorCode ?? `HTTP ${response.status}`, errorCode);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const dispatch = (block: string) => {
      let event = 'message';
      const data: string[] = [];
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data.push(line.slice(5).trim());
      }
      if (data.length === 0) return; // a comment or a heartbeat
      const payload = JSON.parse(data.join('\n'));
      switch (event) {
        case 'text':
          handlers.onText(payload.delta);
          break;
        case 'tool':
          handlers.onTool(payload);
          break;
        case 'done':
          handlers.onDone(payload);
          break;
        case 'error':
          handlers.onError(payload.errorCode);
          break;
      }
    };

    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let end = buffer.indexOf('\n\n');
      while (end >= 0) {
        dispatch(buffer.slice(0, end));
        buffer = buffer.slice(end + 2);
        end = buffer.indexOf('\n\n');
      }
    }
  }
}
